import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";

import { queryAgentDetail, updateStatus } from "./model";
import s from "./AgentsDetail.module.scss";

import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import AccountStatusDialog from "../../components/account-status/AccountStatusDialog";
import { isNullish } from "utils/utils";
import { normalizeAccountStatusDialogData } from "utils/normalizeAccountStatusDialogData";
import StatusWrapper from "components/StatusWrapper/StatusWrapper";

import { PortalContext } from "App";
import InnerTabList from "components/InnerTabList/InnerTabList";
import AgentsDetailInfo from "./AgentsDetailInfo";
import AgentsDetailMerchants from "./AgentsDetailMerchants";
import { getStatusRemarkOptions } from "../../components/account-status/getStatusRemarkOptions";

const PAGE_LOCALE_KEY = "page.agents.agents_detail.";
const DIALOG_LOCALE_KEY = "page.dialogs.";

import responseCodeMapping from "utils/response_code_mapping.json";

const AgentsDetail = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();

  const [selectedTab, setSelectedTab] = useState("detail_info");
  const { enqueueSnackbar } = useSnackbar();

  const stateContext = useContext(PortalContext);
  const { detailPageState, setDetailPageState } = stateContext;
  const [isoAgentId, setIsoAgentId] = useState("");

  const AGENT_STATUS_INIT_STATE = {
    status: "",
    lockAccountType: "",
    remark: "",
  };

  const [statusRemarks, setStatusRemarks] = useState();
  const [agentDetailField, setAgentDetailField] = useState({ email: "" });

  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isInitRef = useRef(true);

  const [accountStatusDialogData, setAccountStatusDialogData] = useState(
    AGENT_STATUS_INIT_STATE,
  );
  const [isAccountStatusDialogOpen, setIsAccountStatusDialogOpen] =
    useState(false);

  useLayoutEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setIsoAgentId(detailPageState[path].isoAgentId);
      setAgentDetailField((state) => ({
        ...state,
        isoAgentId: detailPageState[path].isoAgentId,
        email: detailPageState[path].email,
        status: detailPageState[path].status,
      }));
    } else if (sessionState && sessionState[path]) {
      setIsoAgentId(sessionState[path].isoAgentId);
      setAgentDetailField((state) => ({
        ...state,
        isoAgentId: sessionState[path].isoAgentId,
        email: sessionState[path].email,
        status: sessionState[path].status,
      }));
    } else {
      if (isoAgentId == "") setIsoAgentId("close");
    }
  }, [detailPageState[path]]);

  useLayoutEffect(() => {
    if (isoAgentId == "close") {
      redirectFn("close", { tabId: path });
    } else {
      if (!!isoAgentId && isoAgentId !== "") {
        console.log("isoAgentId : ", isoAgentId);
        initialFetchRemarks();

        if (!isoAgentId) {
          //message "unknown payment id" will be shown
          setDetailLoading(false);
          return;
        }
      }
    }
  }, [isoAgentId]);

  const innerTabContainer = () => {
    const tabList = [
      {
        key: "detail_info",
        label: t(PAGE_LOCALE_KEY + "detailTitle"),
        value: "detail_info",
      },
      {
        key: "detail_merchants",
        label: t(PAGE_LOCALE_KEY + "sectionMerchantsTitle"),
        value: "detail_merchants",
      },
    ];

    const changeSelectedTab = (event, innerTabName) => {
      setSelectedTab(innerTabName);
    };

    return (
      <InnerTabList
        selectedTab={selectedTab}
        handleChangeTab={changeSelectedTab}
        tabList={tabList}
      />
    );
  };

  const initialFetchDetails = async (getIsOudated) => {
    console.log("initialFetchDetails");
    setDetailLoading(true);
    const apiResponse = await queryAgentDetail(isoAgentId);
    if (getIsOudated && getIsOudated()) return;
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            isInitRef.current = false;
            setAgentDetailField((state) => ({
              ...state,
              ...responseBody.data,
            }));
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
            setDatasource({
              total: 0,
              page: 0,
              size: 10,
              data: [],
            });
          }
        }
      }
    }
    setDetailLoading(false);
  };

  const initialFetchRemarks = async () => {
    console.log("initialFetchRemarks ");
    const apiResponse = await getStatusRemarkOptions();

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            setStatusRemarks(responseBody.data);
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }
  };
  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  // ===================== status dialog function ===================== //

  const openAccountStatusDialog = (data) => {
    setAccountStatusDialogData(normalizeAccountStatusDialogData(data));
    setIsAccountStatusDialogOpen(true);
  };

  const closeAccountStatusDialog = () => {
    restoreAccountStatusDialog();
    setIsAccountStatusDialogOpen(false);
  };

  const restoreAccountStatusDialog = () => {
    setAccountStatusDialogData(AGENT_STATUS_INIT_STATE);
  };

  const updatePayload = (fieldInput) => {
    setAccountStatusDialogData(fieldInput);
  };

  const accountStatusDialogValidationCheck = () => {
    const selectedReason =
      accountStatusDialogData.reason ?? accountStatusDialogData.lockAccountType;

    if (
      accountStatusDialogData.status !== "ACTIVE" &&
      accountStatusDialogData.status !== "PENDING"
    ) {
      const hasReason =
        typeof selectedReason === "string" && selectedReason.trim() !== "";
      if (!hasReason) {
        snackbar(
          t(DIALOG_LOCALE_KEY + "changeStatus.msgError") +
            " Reason must not be empty!",
          "error",
        );
      }
      return hasReason;
    }
    return true;
  };

  const updateResultCallback = async () => {
    if (accountStatusDialogValidationCheck()) {
      setSubmitting(true);
      let isUpdateSuccess = false;
      const reasonList = statusRemarks[accountStatusDialogData.status];
      const remarkOthersKey = "OTHERS";
      const selectedReason =
        accountStatusDialogData.reason ?? accountStatusDialogData.lockAccountType;

      let remarksend =
        reasonList && (reasonList[selectedReason] ?? "");
      if (selectedReason == remarkOthersKey)
        remarksend = accountStatusDialogData.remark;

      let payload = {
        isoAgentId: agentDetailField.isoAgentId,
        email: agentDetailField.email,
        isStatusChange:
          accountStatusDialogData.status != agentDetailField.status,
        status: accountStatusDialogData.status,
      };

      if (!isNullish(selectedReason))
        payload = {
          ...payload,
          lockAccountType: selectedReason,
        };
      if (!isNullish(remarksend)) payload = { ...payload, remark: remarksend };

      const apiResponse = await updateStatus(payload);
      if (!apiResponse || !apiResponse?.data) {
        snackbar(t("general.message.networkError"), "error");
      } else {
        switch (apiResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            const responseBody = apiResponse.data;
            if (responseBody.responseCode !== "0000") {
              snackbar(
                t(DIALOG_LOCALE_KEY + "changeStatus.msgFailed") +
                  responseCodeMapping[responseBody.responseCode ?? "error"][
                    "en"
                  ],
                "error",
              );
            } else {
              const nextStatus = accountStatusDialogData.status;
              setAgentDetailField((state) => ({
                ...state,
                status: nextStatus,
              }));
              setDetailPageState((prev) => {
                const newState = {
                  ...(prev ?? {}),
                  [path]: {
                    ...((prev ?? {})[path] ?? {}),
                    status: nextStatus,
                  },
                };
                sessionStorage.setItem("state", JSON.stringify(newState));
                return newState;
              });
              isUpdateSuccess = true;
              snackbar(
                t(
                  DIALOG_LOCALE_KEY +
                    "changeStatus.msgSuccess." +
                    accountStatusDialogData.status.toLowerCase(),
                ),
                "success",
              );
            }
          }
        }
      }
      setSubmitting(false);
      closeAccountStatusDialog();
      if (!isUpdateSuccess) {
        initialFetchDetails();
      }
    }
  };
  const AccountStatusDialogContent = () => {
    return (
      <AccountStatusDialog
        data={accountStatusDialogData}
        remarksList={statusRemarks}
        updatePayload={updatePayload}
      />
    );
  };

  // ===================== status dialog function end ===================== //

  return (
    <div className={s.container}>
      <div className={s.agentDetails}>
        <div className={s.detailItemContainer}>
          <div className={s.titleContent}>
            <div className={s.itemContainer}>
              <span>{isoAgentId + " - " + agentDetailField.email}</span>
            </div>

            {isoAgentId && (
              <div className={s.dialogActionContainer}>
                <StatusWrapper status={agentDetailField.status || "-"} />

                <div>
                  <div className={s.dialogButtonContainer}>
                    <button
                      className={[s.okBtn, s.functionalBtn].join(" ")}
                      disabled={submitting || detailLoading}
                      onClick={() => {
                        return openAccountStatusDialog(agentDetailField);
                      }}
                    >
                      {t(PAGE_LOCALE_KEY + `edit.label`)}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={s.innerTabContainer}>{innerTabContainer()}</div>
      {isoAgentId && (
        <div className={s.tabContainer}>
          <div className={selectedTab != "detail_info" ? s.inactiveTab : ""}>
            <AgentsDetailInfo
              isoAgentId={isoAgentId}
              updateCallback={initialFetchDetails}
            />
          </div>
          <div
            className={selectedTab != "detail_merchants" ? s.inactiveTab : ""}
          >
            <AgentsDetailMerchants
              isoAgentId={isoAgentId}
              isoAgentEmail={agentDetailField?.email}
              agentFirstName={agentDetailField?.firstName}
              agentLastName={agentDetailField?.lastName}
              agentStatus={agentDetailField?.status}
              redirectFn={redirectFn}
            />
          </div>
        </div>
      )}

      {!isoAgentId && (
        <div className={s.contentContainer}>
          <span>{t(PAGE_LOCALE_KEY + "no_isoagentid")}</span>
        </div>
      )}

      <div className={detailLoading ? s.loadingNow : ""}>
        <SimpleDialog
          open={isAccountStatusDialogOpen}
          dialogOncloseFn={closeAccountStatusDialog}
          title={t(DIALOG_LOCALE_KEY + "changeStatus.title")}
          content={AccountStatusDialogContent()}
          positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
          negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
          confirmFn={!submitting ? updateResultCallback : () => {}}
        />
      </div>
    </div>
  );
};

export default React.memo(AgentsDetail);
